import {BackendResponse} from '@common/http/backend-response/backend-response';
import {apiClient} from '@common/http/query-client';
import {Capacitor, PluginListenerHandle, registerPlugin} from '@capacitor/core';

interface AppleDeveloperTokenResponse extends BackendResponse {
  developerToken: string;
}

interface MusicKitInstance {
  authorize: () => Promise<string>;
  unauthorize?: () => Promise<void> | void;
  isAuthorized?: boolean;
  musicUserToken?: string;
  isNative?: boolean;
  bitrate?: number;
  setQueue: (queue: AppleMusicQueueDescriptor) => Promise<unknown>;
  play: () => Promise<unknown>;
  pause: () => void;
  stop?: () => void;
  skipToNextItem?: () => Promise<unknown>;
  skipToPreviousItem?: () => Promise<unknown>;
  seekToTime?: (time: number) => Promise<unknown> | void;
  getPlaybackState?: () => Promise<NativeAppleMusicPlaybackState>;
  currentPlaybackTime?: number;
  currentPlaybackDuration?: number;
  nowPlayingItem?: unknown;
  player?: {
    bitrate?: number;
    nowPlayingItem?: unknown;
    nowPlayingItemIndex?: number;
    currentPlaybackTime?: number;
    currentPlaybackDuration?: number;
    playbackState?: string | number;
    queue?: {
      position?: number;
      items?: unknown[];
      nowPlayingItem?: unknown;
    };
  };
  volume?: number;
  isMuted?: boolean;
  addEventListener?: (
    event: string,
    handler: (event?: unknown) => void,
  ) => void;
  removeEventListener?: (
    event: string,
    handler: (event?: unknown) => void,
  ) => void;
}

export type AppleMusicQueueDescriptor =
  | {song: string}
  | {songs: string[]}
  | {station: string}
  | {url: string};

interface MusicKitApi {
  configure: (options: {
    developerToken: string;
    app: {name: string; build: string; icon?: string};
    bitrate?: number;
    suppressErrorDialog?: boolean;
  }) => unknown;
  getInstance: () => MusicKitInstance;
  PlaybackBitrate?: Record<string, number>;
  Events?: Record<string, string>;
}

interface TrackdropAppleMusicState {
  nativeInstance: MusicKitInstance | null;
  configurePromise: Promise<MusicKitInstance> | null;
  isConfigured: boolean;
  developerTokenExpiresAt: number | null;
  knownAuthorized: boolean;
  knownMusicUserToken: string | null;
}

interface NativeAppleMusicPlugin {
  authorize: (options: {
    developerToken: string;
  }) => Promise<NativeAppleMusicAuthorizationResult>;
  isAuthorized: () => Promise<{authorized?: boolean; status?: string}>;
  setQueue: (options: {song?: string; songs?: string[]; station?: string}) => Promise<void>;
  play: () => Promise<void>;
  pause: () => Promise<void>;
  stop: () => Promise<void>;
  skipToNextItem: () => Promise<NativeAppleMusicPlaybackState | void>;
  skipToPreviousItem: () => Promise<NativeAppleMusicPlaybackState | void>;
  seek: (options: {time: number}) => Promise<void>;
  getPlaybackState: () => Promise<NativeAppleMusicPlaybackState>;
  setVolume: (options: {volume: number}) => Promise<void>;
  setAudioQuality?: (options: {
    quality: AppleMusicAudioQuality;
  }) => Promise<void>;
  addListener: (
    eventName: string,
    listenerFunc: (state: NativeAppleMusicPlaybackState) => void,
  ) => Promise<PluginListenerHandle>;
}

interface NativeAppleMusicAuthorizationResult {
  authorized?: boolean;
  status?: string;
  musicUserToken?: string;
}

interface NativeAppleMusicPlaybackState {
  currentTime?: number;
  duration?: number;
  playbackState?: string;
  songId?: string;
  queuePosition?: number;
  title?: string;
  artistName?: string;
  albumName?: string;
  artworkUrl?: string;
}

const nativeAppleMusic = registerPlugin<NativeAppleMusicPlugin>(
  'TrackdropAppleMusic',
);

export const appleMusicAudioQualities = [
  'High Quality',
  'Lossless',
  'Hi-Res Lossless',
] as const;

export type AppleMusicAudioQuality = (typeof appleMusicAudioQualities)[number];

declare global {
  interface Window {
    MusicKit?: MusicKitApi;
    __trackdropAppleMusic?: TrackdropAppleMusicState;
  }
}

const musicUserTokenStorageKey = 'trackdrop.appleMusicUserToken';
const audioQualityStorageKey = 'trackdrop.appleMusicAudioQuality';
export const appleMusicAuthChangeEvent = 'trackdrop.appleMusicAuthChange';

export async function configureAppleMusic(): Promise<MusicKitInstance> {
  const appleMusic = getAppleMusicState();

  if (isNativeAppleMusicRuntime()) {
    const music = getNativeAppleMusicInstance();
    applyAppleMusicAudioQuality(music);
    return music;
  }

  if (appleMusic.isConfigured && window.MusicKit) {
    return window.MusicKit.getInstance();
  }

  if (appleMusic.configurePromise) {
    return appleMusic.configurePromise;
  }

  appleMusic.configurePromise = (async () => {
    const tokenResponse = await apiClient
      .get<AppleDeveloperTokenResponse>('apple-music/developer-token')
      .then(r => r.data);

    await loadMusicKit();
    if (!window.MusicKit) {
      throw new Error('MusicKit could not be loaded.');
    }

    await Promise.resolve(
      window.MusicKit.configure({
        developerToken: tokenResponse.developerToken,
        bitrate: appleMusicAudioQualityToBitrate(getAppleMusicAudioQuality()),
        suppressErrorDialog: true,
        app: {
          name: 'Trackdrop',
          icon: 'https://trackdrop.musickraze.com/images/apple-music-auth-icon.png',
          build: '1.0.0',
        },
      }),
    );

    appleMusic.isConfigured = true;
    appleMusic.developerTokenExpiresAt = getJwtExpiresAt(
      tokenResponse.developerToken,
    );
    const music = window.MusicKit.getInstance();
    applyAppleMusicAudioQuality(music);
    return music;
  })().finally(() => {
    appleMusic.configurePromise = null;
  });

  return appleMusic.configurePromise;
}

export async function connectAppleMusic(options?: {
  forceAuthorize?: boolean;
}): Promise<string> {
  if (isNativeAppleMusicRuntime()) {
    const token = await authorizeNativeAppleMusic(options);
    rememberMusicUserToken(token);
    return token;
  }

  // Do not call MusicKit.configure() again once the JS instance exists. Newer
  // MusicKit builds expose parts of the instance as read-only, and a second
  // configure during reconnect can throw before authorize() gets a chance to run.
  const music = await configureAppleMusic();

  if (!options?.forceAuthorize && music.isAuthorized && music.musicUserToken) {
    rememberMusicUserToken(music.musicUserToken);
    return music.musicUserToken;
  }

  const token = await withTimeout(
    music.authorize(),
    25_000,
    'Apple Music authorization timed out. Please try again.',
  );
  rememberMusicUserToken(token);
  return token;
}

export async function getAuthorizedAppleMusic(): Promise<MusicKitInstance> {
  if (isNativeAppleMusicRuntime()) {
    const appleMusic = getAppleMusicState();
    const music = getNativeAppleMusicInstance();

    if (appleMusic.knownAuthorized && appleMusic.knownMusicUserToken) {
      applyKnownMusicUserToken(music, appleMusic.knownMusicUserToken);
      return music;
    }

    const status = await nativeAppleMusic.isAuthorized();
    if (status.authorized) {
      const token = await authorizeNativeAppleMusic();
      rememberMusicUserToken(token);
      applyKnownMusicUserToken(music, token);
      return music;
    }

    throw new AppleMusicAuthorizationRequiredError();
  }

  const music = await configureAppleMusic();

  if (music.musicUserToken) {
    rememberMusicUserToken(music.musicUserToken);
    return music;
  }

  const appleMusic = getAppleMusicState();
  if (appleMusic.knownAuthorized && appleMusic.knownMusicUserToken) {
    if (applyKnownMusicUserToken(music, appleMusic.knownMusicUserToken)) {
      return music;
    }

    rememberMusicUserToken(null);
  }

  throw new AppleMusicAuthorizationRequiredError();
}

export async function isAppleMusicAuthorized(): Promise<boolean> {
  const appleMusic = getAppleMusicState();
  if (appleMusic.knownAuthorized) {
    return true;
  }

  try {
    if (isNativeAppleMusicRuntime()) {
      const status = await nativeAppleMusic.isAuthorized();
      appleMusic.knownAuthorized = !!status.authorized;
      return appleMusic.knownAuthorized;
    }

    const music = await configureAppleMusic();
    appleMusic.knownAuthorized = !!music.isAuthorized && !!music.musicUserToken;
    if (appleMusic.knownAuthorized) {
      rememberMusicUserToken(music.musicUserToken);
    }
    return appleMusic.knownAuthorized;
  } catch {
    return false;
  }
}

export function hasKnownAppleMusicAuthorization(): boolean {
  const appleMusic = getAppleMusicState();
  return appleMusic.knownAuthorized && !!appleMusic.knownMusicUserToken;
}

export function preloadAppleMusicForPwa(): void {
  if (!isPwaDisplayMode() || !hasKnownAppleMusicAuthorization()) {
    return;
  }

  configureAppleMusic().catch(err => {
    console.warn('Apple Music PWA preload failed', err);
  });
}

export function getKnownAppleMusicUserToken(): string | null {
  return getAppleMusicState().knownMusicUserToken;
}

export function clearAppleMusicAuthorization(): void {
  rememberMusicUserToken(null);
}

export async function disconnectAppleMusic(): Promise<void> {
  rememberMusicUserToken(null);

  if (isNativeAppleMusicRuntime()) {
    await nativeAppleMusic.pause().catch(() => {});
    getAppleMusicState().nativeInstance = null;
    return;
  }

  const music = window.MusicKit?.getInstance() ?? null;
  if (!music) {
    return;
  }

  music.pause();
  try {
    await music.unauthorize?.();
  } catch {
    // Clearing Trackdrop's stored token is enough to force a fresh session.
  }
}

export function getAppleMusicEventName(name: string): string {
  return window.MusicKit?.Events?.[name] ?? name;
}

export function getAppleMusicAudioQuality(): AppleMusicAudioQuality {
  try {
    const storedQuality = window.localStorage.getItem(audioQualityStorageKey);
    if (storedQuality) {
      const parsed = JSON.parse(storedQuality);
      if (isAppleMusicAudioQuality(parsed)) {
        return parsed;
      }
    }
  } catch {
    //
  }

  return 'High Quality';
}

export function setAppleMusicAudioQuality(
  quality: string,
  music?: MusicKitInstance,
): AppleMusicAudioQuality {
  const normalizedQuality = normalizeAppleMusicAudioQuality(quality);

  try {
    window.localStorage.setItem(
      audioQualityStorageKey,
      JSON.stringify(normalizedQuality),
    );
  } catch {
    //
  }

  applyAppleMusicAudioQuality(music, normalizedQuality);
  return normalizedQuality;
}

export function applyAppleMusicAudioQuality(
  music?: MusicKitInstance,
  quality = getAppleMusicAudioQuality(),
): void {
  if (!music) {
    return;
  }

  const normalizedQuality = normalizeAppleMusicAudioQuality(quality);

  if (music.isNative) {
    void nativeAppleMusic
      .setAudioQuality?.({quality: normalizedQuality})
      .catch(err => {
        console.warn('Apple Music audio quality could not be applied', err);
      });
    return;
  }

  const bitrate = appleMusicAudioQualityToBitrate(normalizedQuality);
  setWritableMusicKitNumber(music, 'bitrate', bitrate);
  setWritableMusicKitNumber(music.player, 'bitrate', bitrate);
}

async function authorizeNativeAppleMusic(options?: {
  forceAuthorize?: boolean;
}): Promise<string> {
  const appleMusic = getAppleMusicState();
  const music = getNativeAppleMusicInstance();

  if (!options?.forceAuthorize && appleMusic.knownMusicUserToken) {
    applyKnownMusicUserToken(music, appleMusic.knownMusicUserToken);
    return appleMusic.knownMusicUserToken;
  }

  const tokenResponse = await apiClient
    .get<AppleDeveloperTokenResponse>('apple-music/developer-token')
    .then(r => r.data);
  const result = await withTimeout(
    nativeAppleMusic.authorize({developerToken: tokenResponse.developerToken}),
    25_000,
    'Apple Music authorization timed out. Please try again.',
  );

  if (!result.authorized || !result.musicUserToken) {
    throw new AppleMusicAuthorizationRequiredError();
  }

  applyKnownMusicUserToken(music, result.musicUserToken);
  return result.musicUserToken;
}

function getNativeAppleMusicInstance(): MusicKitInstance {
  const appleMusic = getAppleMusicState();
  if (appleMusic.nativeInstance) {
    return appleMusic.nativeInstance;
  }

  let currentPlaybackTime = 0;
  let currentPlaybackDuration = 0;
  let playbackState: string | number = 'stopped';
  let playbackStartedAt: number | null = null;
  let queuedSongIds: string[] = [];
  let currentQueueIsStation = false;
  let stationRequiresFreshQueue = false;
  let queuePosition: number | null = null;
  let nowPlayingItem: unknown = null;
  const nativeListeners = new Map<
    string,
    Map<(event?: unknown) => void, PluginListenerHandle>
  >();

  const syncElapsedPlaybackTime = () => {
    if (playbackState !== 'playing' || playbackStartedAt === null) {
      return;
    }

    const elapsed = (Date.now() - playbackStartedAt) / 1000;
    currentPlaybackTime =
      currentPlaybackDuration > 0
        ? Math.min(elapsed, currentPlaybackDuration)
        : elapsed;
  };

  const setPlaybackState = (state: string) => {
    syncElapsedPlaybackTime();
    playbackState = state;
    playbackStartedAt =
      state === 'playing' ? Date.now() - currentPlaybackTime * 1000 : null;
  };

  const resetLocalPlaybackAfterStop = () => {
    currentPlaybackTime = 0;
    currentPlaybackDuration = 0;
    queuedSongIds = [];
    currentQueueIsStation = false;
    stationRequiresFreshQueue = false;
    queuePosition = null;
    nowPlayingItem = null;
    setPlaybackState('stopped');
  };

  const syncNativePlaybackState = (state: NativeAppleMusicPlaybackState) => {
    if (currentQueueIsStation && stationRequiresFreshQueue) {
      return;
    }

    currentPlaybackTime = state.currentTime ?? currentPlaybackTime;
    currentPlaybackDuration = state.duration ?? currentPlaybackDuration;
    playbackState = state.playbackState ?? playbackState;
    queuePosition = state.queuePosition ?? queuePosition;
    const songId = state.songId ?? queuedSongIds[queuePosition ?? -1];
    nowPlayingItem = songId || state.title || state.artistName
      ? {
          id: songId ?? `native-apple-music:${state.title ?? 'unknown'}`,
          attributes: {
            name: state.title,
            artistName: state.artistName,
            albumName: state.albumName,
            artwork: state.artworkUrl ? {url: state.artworkUrl} : undefined,
            playParams: songId ? {id: songId, catalogId: songId} : undefined,
          },
        }
      : nowPlayingItem;
    playbackStartedAt =
      playbackState === 'playing'
        ? Date.now() - currentPlaybackTime * 1000
        : null;
  };

  const addNativeListener = (
    event: string,
    handler: (event?: unknown) => void,
  ) => {
    const eventListeners = nativeListeners.get(event) ?? new Map();
    nativeListeners.set(event, eventListeners);

    void nativeAppleMusic
      .addListener(event, state => {
        syncNativePlaybackState(state);
        handler(state);
      })
      .then(listener => {
        eventListeners.set(handler, listener);
      });
  };

  const removeNativeListener = (
    event: string,
    handler: (event?: unknown) => void,
  ) => {
    const listener = nativeListeners.get(event)?.get(handler);
    if (!listener) {
      return;
    }

    void listener.remove();
    nativeListeners.get(event)?.delete(handler);
  };

  const music: MusicKitInstance = {
    isNative: true,
    isAuthorized: appleMusic.knownAuthorized,
    musicUserToken: appleMusic.knownMusicUserToken ?? undefined,
    authorize: () => authorizeNativeAppleMusic(),
    get currentPlaybackTime() {
      syncElapsedPlaybackTime();
      return currentPlaybackTime;
    },
    get currentPlaybackDuration() {
      return currentPlaybackDuration;
    },
    get nowPlayingItem() {
      return nowPlayingItem;
    },
    get player() {
      syncElapsedPlaybackTime();
      return {
        nowPlayingItem,
        currentPlaybackTime,
        currentPlaybackDuration,
        playbackState,
        queue: {
          position: queuePosition ?? undefined,
          nowPlayingItem: nowPlayingItem ?? undefined,
        },
      };
    },
    setQueue: async queue => {
      if ('url' in queue) {
        throw new Error(
          'Apple Music URL queues are not available in the iOS app.',
        );
      }

      currentQueueIsStation = 'station' in queue;
      stationRequiresFreshQueue = false;
      const songs =
        'songs' in queue
          ? queue.songs
          : ['station' in queue ? queue.station : queue.song];
      queuedSongIds = songs.filter(Boolean);
      const nativeQueue = currentQueueIsStation
        ? {station: queuedSongIds[0]}
        : queuedSongIds.length > 1
          ? {songs: queuedSongIds}
          : {song: queuedSongIds[0]};
      await nativeAppleMusic.setQueue(nativeQueue);
      currentPlaybackTime = 0;
      currentPlaybackDuration = 0;
      queuePosition = 0;
      nowPlayingItem = currentQueueIsStation
        ? null
        : queuedSongIds[0]
        ? {
            id: queuedSongIds[0],
            attributes: {
              playParams: {id: queuedSongIds[0], catalogId: queuedSongIds[0]},
            },
          }
        : null;
      setPlaybackState('paused');
    },
    play: async () => {
      if (currentQueueIsStation && stationRequiresFreshQueue) {
        throw new Error('Apple Music station must be requeued before playback resumes.');
      }

      await nativeAppleMusic.play();
      setPlaybackState('playing');
    },
    pause: () => {
      if (currentQueueIsStation) {
        void nativeAppleMusic.stop();
        currentPlaybackTime = 0;
        currentPlaybackDuration = 0;
        nowPlayingItem = null;
        stationRequiresFreshQueue = true;
        setPlaybackState('stopped');
        return;
      }

      void nativeAppleMusic.pause();
      setPlaybackState('paused');
    },
    stop: () => {
      void nativeAppleMusic.stop();
      resetLocalPlaybackAfterStop();
    },
    skipToNextItem: async () => {
      const state = await nativeAppleMusic.skipToNextItem();
      if (state) {
        syncNativePlaybackState(state);
      }
      if (currentQueueIsStation) {
        setPlaybackState('playing');
        return;
      }
      if (queuePosition !== null) {
        queuePosition = Math.min(queuePosition + 1, queuedSongIds.length - 1);
      }
      currentPlaybackTime = 0;
      const songId = queuedSongIds[queuePosition ?? -1];
      nowPlayingItem = songId
        ? {
            id: songId,
            attributes: {
              playParams: {id: songId, catalogId: songId},
            },
          }
        : nowPlayingItem;
    },
    skipToPreviousItem: async () => {
      const state = await nativeAppleMusic.skipToPreviousItem();
      if (state) {
        syncNativePlaybackState(state);
      }
      if (currentQueueIsStation) {
        setPlaybackState('playing');
        return;
      }
      if (queuePosition !== null) {
        queuePosition = Math.max(queuePosition - 1, 0);
      }
      currentPlaybackTime = 0;
      const songId = queuedSongIds[queuePosition ?? -1];
      nowPlayingItem = songId
        ? {
            id: songId,
            attributes: {
              playParams: {id: songId, catalogId: songId},
            },
          }
        : nowPlayingItem;
    },
    seekToTime: async time => {
      await nativeAppleMusic.seek({time});
      currentPlaybackTime = time;
      playbackStartedAt =
        playbackState === 'playing'
          ? Date.now() - currentPlaybackTime * 1000
          : null;
    },
    getPlaybackState: async () => {
      const state = await nativeAppleMusic.getPlaybackState();
      syncNativePlaybackState(state);
      return state;
    },
    addEventListener: addNativeListener,
    removeEventListener: removeNativeListener,
  };

  appleMusic.nativeInstance = music;
  return music;
}

function normalizeAppleMusicAudioQuality(
  quality: string,
): AppleMusicAudioQuality {
  const normalized = quality.trim().toLowerCase();
  const matchedQuality = appleMusicAudioQualities.find(
    candidate => candidate.toLowerCase() === normalized,
  );

  return matchedQuality ?? 'High Quality';
}

function isAppleMusicAudioQuality(
  quality: unknown,
): quality is AppleMusicAudioQuality {
  return (
    typeof quality === 'string' &&
    appleMusicAudioQualities.includes(quality as AppleMusicAudioQuality)
  );
}

function appleMusicAudioQualityToBitrate(
  quality: AppleMusicAudioQuality,
): number {
  switch (quality) {
    case 'Hi-Res Lossless':
      return 9216;
    case 'Lossless':
      return 1411;
    case 'High Quality':
    default:
      return window.MusicKit?.PlaybackBitrate?.HIGH ?? 256;
  }
}

function setWritableMusicKitNumber(
  target: unknown,
  property: string,
  value: number,
): void {
  if (!target || typeof target !== 'object') {
    return;
  }

  try {
    (target as Record<string, number>)[property] = value;
  } catch {
    // Some MusicKit builds expose bitrate as read-only and will keep using the
    // device/account playback policy.
  }
}

export function isNativeAppleMusicRuntime(): boolean {
  return (
    Capacitor.isNativePlatform?.() ||
    navigator.userAgent.includes('TrackdropCapacitor')
  );
}

function applyKnownMusicUserToken(
  music: MusicKitInstance,
  token: string,
): boolean {
  try {
    music.musicUserToken = token;
    music.isAuthorized = true;
  } catch (err) {
    console.warn('Apple Music stored token could not be restored', err);
  }

  return music.musicUserToken === token;
}

export class AppleMusicAuthorizationRequiredError extends Error {
  constructor() {
    super('Apple Music is not authorized.');
    this.name = 'AppleMusicAuthorizationRequiredError';
  }
}

function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  message: string,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(
      () => reject(new Error(message)),
      timeoutMs,
    );
    promise.then(
      value => {
        window.clearTimeout(timeout);
        resolve(value);
      },
      error => {
        window.clearTimeout(timeout);
        reject(error);
      },
    );
  });
}

function loadMusicKit(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.MusicKit) {
      resolve();
      return;
    }

    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error('MusicKit could not be loaded.'));
    }, 8000);

    const resolveWhenReady = () => {
      if (!window.MusicKit) {
        return;
      }

      cleanup();
      resolve();
    };

    const cleanup = () => {
      window.clearTimeout(timeout);
      document.removeEventListener('musickitloaded', resolveWhenReady);
    };

    document.addEventListener('musickitloaded', resolveWhenReady);

    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[data-trackdrop-musickit]',
    );
    if (existingScript) {
      if (existingScript.dataset.trackdropMusickitStatus === 'error') {
        existingScript.remove();
      } else {
        existingScript.addEventListener('load', resolveWhenReady, {once: true});
        existingScript.addEventListener(
          'error',
          () => {
            existingScript.dataset.trackdropMusickitStatus = 'error';
            cleanup();
            reject(new Error('MusicKit could not be loaded.'));
          },
          {once: true},
        );
        window.setTimeout(resolveWhenReady, 0);
        return;
      }
    }

    const script = document.createElement('script');
    script.src = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
    script.async = true;
    script.dataset.trackdropMusickit = 'true';
    script.dataset.trackdropMusickitStatus = 'loading';
    script.onload = () => {
      script.dataset.trackdropMusickitStatus = 'ready';
      resolveWhenReady();
    };
    script.onerror = () => {
      script.dataset.trackdropMusickitStatus = 'error';
      cleanup();
      reject(new Error('Could not load MusicKit.'));
    };
    document.head.appendChild(script);
  });
}

function isPwaDisplayMode(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & {standalone?: boolean}).standalone === true
  );
}

function getStoredMusicUserToken(): string | null {
  try {
    const localToken = window.localStorage.getItem(musicUserTokenStorageKey);
    if (localToken) {
      return JSON.parse(localToken) as string;
    }

    return window.sessionStorage.getItem(musicUserTokenStorageKey);
  } catch {
    return null;
  }
}

function rememberMusicUserToken(token?: string | null): void {
  const appleMusic = getAppleMusicState();
  appleMusic.knownMusicUserToken = token || null;
  appleMusic.knownAuthorized = !!appleMusic.knownMusicUserToken;

  try {
    if (appleMusic.knownMusicUserToken) {
      window.localStorage.setItem(
        musicUserTokenStorageKey,
        JSON.stringify(appleMusic.knownMusicUserToken),
      );
    } else {
      window.localStorage.removeItem(musicUserTokenStorageKey);
    }
    window.sessionStorage.removeItem(musicUserTokenStorageKey);
  } catch {
    // Browser storage can be unavailable in private or restricted browsers.
  }

  try {
    window.dispatchEvent(
      new CustomEvent(appleMusicAuthChangeEvent, {
        detail: {isAuthorized: appleMusic.knownAuthorized},
      }),
    );
  } catch {
    //
  }
}

function getAppleMusicState(): TrackdropAppleMusicState {
  if (!window.__trackdropAppleMusic) {
    const storedMusicUserToken = getStoredMusicUserToken();
    window.__trackdropAppleMusic = {
      nativeInstance: null,
      configurePromise: null,
      isConfigured: false,
      developerTokenExpiresAt: null,
      knownAuthorized: !!storedMusicUserToken,
      knownMusicUserToken: storedMusicUserToken,
    };
  }

  return window.__trackdropAppleMusic;
}

function isDeveloperTokenExpiringSoon(expiresAt: number | null): boolean {
  if (!expiresAt) {
    return false;
  }

  return expiresAt <= Date.now() + 60_000;
}

function getJwtExpiresAt(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '=',
    );
    const parsed = JSON.parse(window.atob(padded)) as {exp?: number};
    return parsed.exp ? parsed.exp * 1000 : null;
  } catch {
    return null;
  }
}
